import * as THREE from 'three';
import { getTableLayout, ballRadius } from '../sim/world.js';
import { registerFeltMaterial } from './theme.js';

/**
 * クッション（cushion）の正しい台形形状（自前・GPL 非依存）。
 *
 * 実機のクッションは「内側エッジ（ボール接触面）は直線」「両端はポケットに向かって
 * 斜めカット」された台形（ジョー）形状を持つ。tailuge tablemesh の単純な Box では
 * このジョーが表現できないため、本モジュールが ExtrudeGeometry で各 rail segment
 * ごとに台形 cushion を描画する。
 *
 * 物理（衝突判定）は engine 側の TableGeometry/PocketGeometry を使うので、本描画は
 * 視覚専用。
 *
 * 各 cushion 台形の構造（上から見て）:
 *   - 内側エッジ（cushion 内側、ボール接触面）: rail segment の from..to を結ぶ直線
 *   - 外側エッジ（レール接触面）: rail segment の両端から CUSHION_END_CUT だけ
 *     内側に絞り込まれた直線 → 両端が斜めにカットされる
 */

// 木枠レール / ポケット黒穴と整合させるため export（SSOT）。
export const CUSHION_THICKNESS = ballRadius * 2; // tailuge の d = R/0.5 = 2R と同じ
const CUSHION_HEIGHT = ballRadius * 1.5; // tailuge の h = R*0.75/0.5 = 1.5R
const CUSHION_Z_BOTTOM = -ballRadius;
export const CUSHION_END_CUT = ballRadius * 1.4; // 両端の斜めカット量（V ジョー深さ）
// クッション鼻先（内側面）を seg.fixed（=ボール中心の反射線 tableX/Y）から外側へ R 寄せる。
// 物理はボール「中心」が tableX/Y で反射 → ボール外周は tableX/Y + R に到達するため、
// 鼻先を +R に置かないとボールがクッションへ R ぶん食い込んで見える（クロス端 ±(half+R) とも一致）。
export const CUSHION_NOSE_OFFSET = ballRadius;

const CUSHION_MAT = new THREE.MeshStandardMaterial({
  color: 0x196b36,
  roughness: 0.96,
  metalness: 0.0,
  envMapIntensity: 0.18,
});
// ラシャ色テーマへ登録（クッションは bed より少し暗い同系色で連動）。
registerFeltMaterial(CUSHION_MAT, 'cushion');

const signedArea = (pts: ReadonlyArray<{ x: number; y: number }>): number => {
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const a = pts[i];
    const b = pts[j];
    if (!a || !b) continue;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
};

export const addTableCushions = (scene: THREE.Scene): void => {
  const { rails } = getTableLayout();

  for (const seg of rails) {
    const sign = seg.fixed > 0 ? 1 : -1;
    // 鼻先（球が当たる面）= ボール中心反射線 + R（クロス端と一致）。
    const inner = seg.fixed + sign * CUSHION_NOSE_OFFSET;
    const outer = inner + sign * CUSHION_THICKNESS;

    // 内側エッジ = seg.from..seg.to（ナックル位置で終端）
    // 外側エッジ = ナックル位置を越えて両端方向に CUSHION_END_CUT 延長
    let verts: { x: number; y: number }[];
    if (seg.axis === 'x') {
      verts = [
        { x: seg.from, y: inner },
        { x: seg.to, y: inner },
        { x: seg.to + CUSHION_END_CUT, y: outer },
        { x: seg.from - CUSHION_END_CUT, y: outer },
      ];
    } else {
      verts = [
        { x: inner, y: seg.from },
        { x: inner, y: seg.to },
        { x: outer, y: seg.to + CUSHION_END_CUT },
        { x: outer, y: seg.from - CUSHION_END_CUT },
      ];
    }

    // Three.js Shape は CCW を期待する。負の signed area なら反転。
    if (signedArea(verts) < 0) verts = [...verts].reverse();

    const shape = new THREE.Shape();
    verts.forEach((v, i) => {
      if (i === 0) shape.moveTo(v.x, v.y);
      else shape.lineTo(v.x, v.y);
    });
    shape.closePath();

    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: CUSHION_HEIGHT,
      bevelEnabled: false,
    });
    geo.translate(0, 0, CUSHION_Z_BOTTOM);

    const mesh = new THREE.Mesh(geo, CUSHION_MAT);
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    scene.add(mesh);
  }
};
